import React from "react";
import {
  Bot,
  Workflow,
  PhoneIncoming,
  Database,
  Magnet,
  CalendarCheck,
} from "lucide-react";

const WORKFLOW_IMG =
  "https://static.prod-images.emergentagent.com/jobs/cf4fc7f2-a73a-4fe6-9ce3-8070ac060fc8/images/1fd72961aa1fa29b45b4d8cc9726e5b8eff5678bd21a074f0923928643d76895.png";

const items = [
  {
    icon: Bot,
    title: "AI Assistants",
    desc: "Friendly chat & voice agents that answer enquiries 24/7, give quotes, and qualify leads — all in your tone.",
    color: "bg-[var(--pink)]",
    text: "text-white",
    testid: "service-ai-assistants",
  },
  {
    icon: Workflow,
    title: "Workflow Automation",
    desc: "Stop copy-pasting between WhatsApp, email, spreadsheets and your calendar. We wire it up once, you reap rewards forever.",
    color: "bg-white",
    text: "text-ink",
    testid: "service-workflow-automation",
    image: WORKFLOW_IMG,
    wide: true,
  },
  {
    icon: PhoneIncoming,
    title: "Voice Agents",
    desc: "AI receptionist that picks up while you're on a roof, under a sink, or on a haircut. Never miss a job again.",
    color: "bg-[var(--blue)]",
    text: "text-white",
    testid: "service-voice-agents",
  },
  {
    icon: Database,
    title: "CRM Integrations",
    desc: "Plug AI into Jobber, ServiceM8, HubSpot, Pipedrive, Google Sheets — or whatever spreadsheet you already love.",
    color: "bg-white",
    text: "text-ink",
    testid: "service-crm-integrations",
  },
  {
    icon: Magnet,
    title: "Lead Capture",
    desc: "Convert your Google Business profile, Facebook page and website into a steady stream of qualified enquiries.",
    color: "bg-[var(--yellow)]",
    text: "text-ink",
    testid: "service-lead-capture",
  },
  {
    icon: CalendarCheck,
    title: "Booking & Scheduling",
    desc: "Two-way calendar sync, smart routing by postcode, and automatic reminders that slash no-shows.",
    color: "bg-white",
    text: "text-ink",
    testid: "service-booking",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="border-b-2 border-ink py-24 lg:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="uppercase text-sm font-bold tracking-[0.25em] text-[var(--pink)]">
              What we do
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
              Boring jobs, <br className="hidden sm:block" />
              <span className="text-[var(--blue)]">automated.</span>
            </h2>
          </div>
          <p className="max-w-md text-stone-700 text-lg">
            Six core services, designed for one-person businesses and small
            teams. Mix and match — we'll recommend what'll actually move the
            needle.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={s.testid}
                className={`relative border-2 border-ink rounded-3xl p-7 shadow-brutal shadow-brutal-hover ${s.color} ${s.text} ${
                  s.wide ? "lg:col-span-2 lg:row-span-1" : ""
                } ${i === 0 ? "tilt-left" : i === 2 ? "tilt-right" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div
                    className={`h-12 w-12 rounded-xl border-2 border-ink flex items-center justify-center ${
                      s.color === "bg-white"
                        ? "bg-[var(--yellow)]"
                        : "bg-white"
                    } text-ink`}
                  >
                    <Icon size={22} />
                  </div>
                  <span className="font-display text-3xl font-black opacity-50">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display mt-5 text-2xl font-extrabold">
                  {s.title}
                </h3>
                <p
                  className={`mt-3 ${s.text === "text-white" ? "text-white/90" : "text-stone-700"} leading-relaxed`}
                >
                  {s.desc}
                </p>
                {s.image && (
                  <img
                    src={s.image}
                    alt="Workflow automation"
                    className="mt-5 w-full h-44 object-cover rounded-xl border-2 border-ink"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
